import { useCallback, useEffect, useRef } from 'react';

/**
 * Below this, a press is read as a tap: the recording latches on and waits for a
 * second tap to stop. Above it, the press is a walkie-talkie hold and the release
 * ends the turn. 400ms is long enough that a normal tap never trips it and short
 * enough that a learner who means to hold never wonders why nothing happened.
 */
export const HOLD_THRESHOLD_MS = 400;

/**
 * Learners release the button the instant they stop talking, which clips the last
 * syllable. Keep the mic open for a beat after the release — the same trick the
 * realtime toggle uses for its stop button.
 */
export const HOLD_RELEASE_TAIL_MS = 250;

/**
 * A pointer gesture synthesises a click right after its release. Anything arriving
 * this soon after a real pointer event is that echo; anything later is a genuine
 * activation — a keyboard press, or a browser with no pointer events at all, where
 * the button has to keep working as the plain click-toggle it has always been.
 */
const POINTER_CLICK_ECHO_MS = 700;

const GESTURE_IDLE = 'idle';
const GESTURE_STARTING = 'starting'; // press opened the mic; release decides tap vs hold
const GESTURE_STOPPING = 'stopping'; // press landed on an already-recording button

/**
 * Press-and-hold recording on top of the existing tap-to-start/tap-to-stop button.
 *
 * One gesture handler drives both interactions, because the two modes can only be
 * told apart after the fact — a hold is a tap that has not been released yet. The
 * mic opens on press either way, so nothing a learner says while pressing is lost,
 * and the release decides what that press meant:
 *
 *   press → release under 400ms  → tap mode: recording stays on, a later tap stops it
 *   press → release over 400ms   → hold mode: the release ends the turn
 *   press → pointercancel        → the gesture became a scroll or was interrupted:
 *                                  the recording is thrown away, not submitted
 *
 * Pointer events cover mouse, touch and pen in one path, and the pointer is captured
 * so a finger that drifts off the button still delivers its release here. Keyboard
 * activation cannot be a hold at all, so it falls through to plain tap toggling.
 *
 * @param {object} params
 * @param {boolean} params.isRecording - Whether the mic is currently open.
 * @param {boolean} [params.disabled] - Ignore all gestures (turn in flight).
 * @param {() => void} params.onStart - Open the mic.
 * @param {() => void} params.onStop - Close the mic and submit the recording.
 * @param {() => void} params.onCancel - Close the mic and discard the recording.
 * @param {() => void} [params.onReleaseCommit] - Called synchronously inside the release
 *   gesture, before the tail delays the stop. Anything that must run on a user gesture's
 *   own call stack (iOS audio unlocking) belongs here, not in onStop.
 * @param {(holding: boolean) => void} [params.onHoldingChange] - Hold-mode UI hint.
 * @returns {{onPointerDown, onPointerUp, onPointerCancel, onClick, onContextMenu}}
 */
export default function usePressAndHoldRecording({
  isRecording,
  disabled = false,
  onStart,
  onStop,
  onCancel,
  onReleaseCommit,
  onHoldingChange,
}) {
  const gestureRef = useRef(GESTURE_IDLE);
  const pressedAtRef = useRef(0);
  const lastPointerAtRef = useRef(0);
  const holdTimerRef = useRef(null);
  const tailTimerRef = useRef(null);

  const setHolding = useCallback((holding) => {
    if (onHoldingChange) onHoldingChange(holding);
  }, [onHoldingChange]);

  const clearHoldTimer = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }, []);

  const clearTailTimer = useCallback(() => {
    if (tailTimerRef.current) {
      clearTimeout(tailTimerRef.current);
      tailTimerRef.current = null;
    }
  }, []);

  /**
   * @param {boolean} [keepHolding] - Leave the hold hint up while the release tail
   *   finishes the recording, so it doesn't flash back to "tap again" for a frame.
   */
  const endGesture = useCallback((keepHolding = false) => {
    gestureRef.current = GESTURE_IDLE;
    pressedAtRef.current = 0;
    clearHoldTimer();
    if (!keepHolding) setHolding(false);
  }, [clearHoldTimer, setHolding]);

  useEffect(() => () => {
    clearHoldTimer();
    clearTailTimer();
  }, [clearHoldTimer, clearTailTimer]);

  const handlePointerDown = useCallback((event) => {
    if (disabled) return;
    // Only the primary button opens the mic — a right-click must never record.
    if (event.button !== undefined && event.button !== 0) return;

    // A press that lands while the release tail is still running belongs to the
    // next turn, not this one: close the previous turn immediately so the two
    // recordings can't overlap.
    if (tailTimerRef.current) {
      clearTailTimer();
      onStop();
      return;
    }

    lastPointerAtRef.current = Date.now();

    if (event.currentTarget.setPointerCapture && event.pointerId !== undefined) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (error) {
        // Capture is a nicety (it keeps a drifting finger's release on the button);
        // browsers that refuse it still deliver pointerup somewhere useful.
      }
    }

    if (isRecording) {
      gestureRef.current = GESTURE_STOPPING;
      return;
    }

    gestureRef.current = GESTURE_STARTING;
    pressedAtRef.current = Date.now();
    onStart();

    // Once the press outlives the tap window it is unambiguously a hold. Announcing it
    // as it happens is what teaches the gesture: the learner sees the button switch to
    // "release when you're done" while their finger is still on it.
    clearHoldTimer();
    holdTimerRef.current = setTimeout(() => {
      holdTimerRef.current = null;
      if (gestureRef.current === GESTURE_STARTING) setHolding(true);
    }, HOLD_THRESHOLD_MS);
  }, [disabled, isRecording, onStart, onStop, clearTailTimer, clearHoldTimer, setHolding]);

  const handlePointerUp = useCallback(() => {
    lastPointerAtRef.current = Date.now();

    const gesture = gestureRef.current;
    if (gesture === GESTURE_IDLE) return;

    if (gesture === GESTURE_STOPPING) {
      endGesture();
      onStop();
      return;
    }

    const heldForMs = Date.now() - pressedAtRef.current;

    // A tap leaves the recording latched on; the learner stops it with a second tap.
    if (heldForMs < HOLD_THRESHOLD_MS) {
      endGesture();
      return;
    }

    // The release is the last moment this turn spends on a user gesture's call stack.
    if (onReleaseCommit) onReleaseCommit();

    endGesture(true);
    clearTailTimer();
    tailTimerRef.current = setTimeout(() => {
      tailTimerRef.current = null;
      setHolding(false);
      onStop();
    }, HOLD_RELEASE_TAIL_MS);
  }, [endGesture, onStop, onReleaseCommit, clearTailTimer, setHolding]);

  /**
   * The browser took the gesture away — a scroll, a system UI, a lost pointer. The
   * learner never meant to record, so the audio is dropped instead of submitted.
   */
  const handlePointerCancel = useCallback(() => {
    const gesture = gestureRef.current;
    if (gesture === GESTURE_IDLE) return;

    endGesture();
    if (gesture === GESTURE_STARTING) onCancel();
  }, [endGesture, onCancel]);

  /**
   * Pointer events already handled the mouse and touch presses, and the click they
   * synthesise afterwards would toggle the mic a second time. What has to survive
   * here is activation with no pointer behind it: Enter/Space on the focused button,
   * and browsers that never fire pointer events in the first place.
   */
  const handleClick = useCallback((event) => {
    if (disabled) return;
    if (Date.now() - lastPointerAtRef.current < POINTER_CLICK_ECHO_MS) return;

    if (isRecording) {
      onStop();
    } else {
      onStart();
    }
  }, [disabled, isRecording, onStart, onStop]);

  /**
   * A long press on touch pops the context menu / text-selection callout on top of
   * the button, which is exactly the gesture this feature is built on.
   */
  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
  }, []);

  return {
    onPointerDown: handlePointerDown,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
    onClick: handleClick,
    onContextMenu: handleContextMenu,
  };
}
